import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useKey } from 'rooks';
import useImage from 'use-image';
import ReactPlayer from 'react-player';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@material-ui/icons/ArrowForwardIosRounded';
import ImageRoundedIcon from '@material-ui/icons/ImageRounded';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { LoadingProgress } from '../LoadingProgress';
import { KeyboardEvent } from '../../services/KeyboardEventsService';
import { KEYBOARD_CONTEXT_MEDIA, KEYBOARD_CONTEXT_MEDIA_SLIDER } from '../../constants/keyboard.constants';

import { MediaItemSliderProps } from '../../types';
import './media.item.slider.scss';

export const ChallengeMediaItemSlider = ({
  items,
  mediaZoomIndex,
  changeMediaZoomIndex,
  mediaZoomClose,
  onFavorite,
}: MediaItemSliderProps): JSX.Element => {
  const thumbnail = items[mediaZoomIndex]?.media?.preview || items[mediaZoomIndex]?.thumbnail;
  let isFavorite = items[mediaZoomIndex]?.favorite;
  const type = items[mediaZoomIndex]?.media?.type || items[mediaZoomIndex]?.type;
  const [media, status] = useImage(thumbnail);
  const isVerticalMedia = media ? (media.height > media.width) : false;
  const itemFullImage = items[mediaZoomIndex]?.media?.originalFileUrl;

  const noOtherActiveElements = () => {
    const activeElementType = (document.activeElement as HTMLInputElement).type;
    return activeElementType !== 'textarea' && activeElementType !== 'input' && activeElementType !== 'text';
  };

  const handleFavoriteClick = (mediaId: string) => {
    isFavorite = !isFavorite;
    onFavorite?.(mediaId, isFavorite);
  };

  const previousItem = (e?: any) => {
    if (noOtherActiveElements()) {
      e && e.preventDefault();
      e && e.stopPropagation();
    }

    if (mediaZoomIndex > 0 && noOtherActiveElements()) {
      changeMediaZoomIndex(mediaZoomIndex - 1);
    }
  };

  const nextItem = (e?: any) => {
    if (noOtherActiveElements()) {
      e && e.preventDefault();
      e && e.stopPropagation();
    }

    if (mediaZoomIndex < items.length - 1 && noOtherActiveElements()) {
      changeMediaZoomIndex(mediaZoomIndex + 1);
    }
  };

  const mediaClickHandler = (e?: any) => {
    e && e.preventDefault();
    e && e.stopPropagation();
  };

  const closeSlider = () => {
    KeyboardEvent.setContext(KEYBOARD_CONTEXT_MEDIA);
    mediaZoomClose();
  };

  const openFullSize = () => {
    window.open(itemFullImage);
  };

  useKey(37, previousItem);
  useKey(39, nextItem);
  useKey(27, () => closeSlider());
  useKey(13, () => KeyboardEvent.trigger(KEYBOARD_CONTEXT_MEDIA_SLIDER, () => openFullSize()));

  useEffect(() => {
    if (typeof mediaZoomIndex === 'number') {
      KeyboardEvent.setContext(KEYBOARD_CONTEXT_MEDIA_SLIDER);
    }
  }, [mediaZoomIndex]);

  useEffect(() => {
    if (!items[mediaZoomIndex]) {
      closeSlider();
    }
  }, [items]);

  return (
    <div className="ChallengeMediaItemSlider">
      <div
        className="media-item-slider-backdrop"
        onClick={closeSlider}
      />
      <div
        className="media-item-slider-close"
        onClick={closeSlider}
      >
        <CloseRoundedIcon />
      </div>
      <div
        className={clsx('media-item-slider-container', {
          'vertical-image': isVerticalMedia,
          'horizontal-image': !isVerticalMedia,
          loaded: status === 'loaded',
        })}
      >
        <div
          className={clsx('media-item-slider-arrow-left', { disable: mediaZoomIndex === 0 })}
          onClick={previousItem}
        >
          <ArrowBackIosRoundedIcon />
        </div>
        <div className="media-item-preview">
          {items[mediaZoomIndex].mediaId && onFavorite && (
            <div
              className="media-item-slider-favorite"
              onClick={() => handleFavoriteClick(items[mediaZoomIndex].mediaId)}
            >
              {isFavorite ? (
                <FavoriteIcon />
              ) : (
                <FavoriteBorderIcon />
              )}
            </div>
          )}
          <div
            className={clsx('media-item-preview-content')}
            onClick={mediaClickHandler}
          >
            {type === 'video' && (
              <ReactPlayer
                url={`${thumbnail}`}
                controls
              />
            )}
            {(type !== 'video') && (
              <>
                {media && (status === 'loaded') && (
                  <div className="media-item-image">
                    <img
                      src={media.src}
                      alt={media?.title}
                    />
                  </div>
                )}
                {!!(status === 'loading') && (
                  <LoadingProgress />
                )}
                {!!(status === 'failed') && (
                  <div className="media-item-no-image">
                    <ImageRoundedIcon />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div
          className={clsx('media-item-slider-arrow-right', { disable: mediaZoomIndex === items.length - 1 })}
          onClick={nextItem}
        >
          <ArrowForwardIosRoundedIcon />
        </div>
        <div className="media-item-slider-status">
          <p>
            {mediaZoomIndex + 1}
            <span>/</span>
            {items.length}
          </p>
        </div>
      </div>
    </div>
  );
};
